import React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ClarificationModeFields } from '@/components/clarification-mode-fields';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useClarificationPrefsStore,
  type ClarificationMode,
} from '@/stores/clarification-prefs-store';

// Short label shown directly on the footer dropdown trigger (replacing
// the old icon, so the active mode is visible at a glance).
const MODE_TRIGGER_LABEL: Record<ClarificationMode, string> = {
  auto: 'Proceed without asking',
  ask_with_timeout: 'Ask with timeout',
  ask_and_wait: 'Ask and wait',
};

// Per-mode tooltip copy for the footer trigger button.
const MODE_TOOLTIP: Record<ClarificationMode, string> = {
  auto: 'Clarifying questions: proceed without asking',
  ask_with_timeout: 'Clarifying questions: ask with timeout',
  ask_and_wait: 'Clarifying questions: ask and wait',
};

/**
 * Clarification settings popover in the chat-input footer (next to the
 * model picker). Picks one of three modes for how agents handle a
 * clarifying question mid-run; the timeout dropdown is nested under
 * "Ask with timeout". Writes to ``useClarificationPrefsStore`` — read by
 * the Run button at dispatch and pushed live over the chat WS via
 * ``use-general-chat``.
 *
 * When the loaded workflow declares a ``default_clarification_mode`` the
 * store carries a session-scoped per-workflow override (seeded from that
 * default); this component then reads and writes THAT session state, so
 * pill edits apply to the current workflow only and the user's persisted
 * personal preference is untouched.
 */
export const ClarificationSettingsButton: React.FC = () => {
  // Primitive selectors (not the effectiveClarificationPrefs object) so
  // zustand's Object.is equality prevents needless re-renders.
  const mode = useClarificationPrefsStore(
    (s) => s.workflowSession?.mode ?? s.mode,
  );
  const isWorkflowScoped = useClarificationPrefsStore(
    (s) => s.workflowSession !== null,
  );

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type='button'
              aria-label='Clarification mode'
              className={cn(
                'flex items-center gap-1 rounded-2xl border border-zinc-200 bg-white px-2.5 py-1 text-[11px] text-zinc-600 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400',
                'hover:bg-zinc-50 dark:hover:bg-zinc-700',
              )}
            >
              {MODE_TRIGGER_LABEL[mode]}
              <ChevronDown className='size-3' />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side='top'>{MODE_TOOLTIP[mode]}</TooltipContent>
      </Tooltip>
      <PopoverContent side='top' align='start' className='w-80'>
        <div className='flex flex-col gap-3'>
          <h4 className='text-sm font-medium'>
            How should agents proceed when they have clarifying questions mid
            run?
          </h4>

          {isWorkflowScoped && (
            <p className='text-xs text-zinc-500 dark:text-zinc-400'>
              Started from this workflow&apos;s default. Changes here apply to
              this workflow session only.
            </p>
          )}

          <div className='border-t border-zinc-200 dark:border-zinc-700' />

          <ClarificationModeFields idPrefix='clar-mode' />
        </div>
      </PopoverContent>
    </Popover>
  );
};
